import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LangContext";
import { formatPrice } from "../lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  return (
    <>
      <div
        className={`fixed inset-0 bg-ink/40 z-[60] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-canvas z-[70] shadow-2xl transition-transform duration-300 flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-brand-secondary/30">
          <h3 className="font-heading text-2xl text-ink">{t("cart_title")}</h3>
          <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-ink hover:text-brand-primary">
            <X size={22} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
            <p className="text-ink-soft">{t("cart_empty")}</p>
            <button
              data-testid="cart-empty-cta"
              onClick={() => { setOpen(false); navigate("/tienda"); }}
              className="bg-brand-primary text-white px-6 py-3 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors"
            >
              {t("cart_empty_cta")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <div key={item.id} data-testid={`cart-item-${item.id}`} className="flex gap-4">
                  <img
                    src={item.images?.[0]}
                    alt={item[`name_${lang}`]}
                    className="w-20 h-24 object-cover"
                  />
                  <div className="flex-1">
                    <p className="font-heading text-lg text-ink leading-tight">{item[`name_${lang}`]}</p>
                    <p className="text-brand-primary text-sm mt-1">{formatPrice(item.price)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} className="border border-brand-secondary p-1 hover:bg-canvas-deep" data-testid={`cart-dec-${item.id}`}>
                        <Minus size={12} />
                      </button>
                      <span className="text-sm w-5 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1)} className="border border-brand-secondary p-1 hover:bg-canvas-deep" data-testid={`cart-inc-${item.id}`}>
                        <Plus size={12} />
                      </button>
                      <button onClick={() => removeItem(item.id)} className="ml-auto text-ink-soft hover:text-destructive" data-testid={`cart-remove-${item.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-brand-secondary/30 px-6 py-5 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-ink-soft uppercase tracking-wide text-sm">{t("cart_total")}</span>
                <span data-testid="cart-total" className="font-heading text-2xl text-ink">{formatPrice(total)}</span>
              </div>
              <button
                data-testid="cart-checkout-btn"
                onClick={() => { setOpen(false); navigate("/pedido"); }}
                className="w-full bg-brand-primary text-white py-4 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors"
              >
                {t("checkout_title")}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};
